import { Box, Button, FormLabel, Grid, Typography, Stack, Tooltip } from "@mui/material";
import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { CommonSelect, CommonPaginationContainer } from "../common";
import { CommonButton } from "../common/commonButton";
import { Months, Years } from "../../utils/constants"
import { DataGrid, GridToolbarContainer } from "@mui/x-data-grid";
import { useStyles } from '../bankTransactions/bankTranscationContainerStyles'
import { getPaginationCount, customDateFormat } from '../CashAllocation/utils'
import { AgedReports, RunAgeDeptReport, DownloadPolicyData, GetAgedDeptYears, GetCalculationFiles } from "../../Services/api";
import format from "date-fns/format";
import instance from "../../redux/api";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PendingIcon from '@mui/icons-material/Pending';
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { CircularProgress } from "@mui/material";
import ReplayIcon from '@mui/icons-material/Replay';
import { toast } from "react-toastify";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../utils/routes";

const tooltipStyle = {
  backgroundColor: "#fff",
  color: '#3b3b3b',
  fontSize: '10px',
  border: '1px solid #3b3b3b',
  borderRadius: '0px',
  padding: '6px 6px',
  maxWidth: 300,
  whiteSpace: 'pre-line',
}

export const AgedReport = () => {
  const toggle = useSelector((state) => state.toggleSideMenu);
  const { userData } = useSelector((state) => state?.user);

  const [agedReportData, setAgedReportData] = useState([]);
  //For pagination
  const [paginationCount, setPaginationCount] = useState(0);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  const [downloadingData, setDownloadingData] = useState([]);
  const [isReportGenerating, setIsReportGenerating] = useState(false);
  const [processingData, setProcessingData] = useState([]);
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  const agedReportContainer = {
    width: toggle?.isOpen ? "calc(100vw - 305px)" : "calc(100vw - 100px)",
    overFlow: "auto",
  };
  
  const agedReportText = {
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "400",
    marginTop: 5,
  };

  const [month, setMonth] = useState(null)
  const [year, setYear] = useState(null)
  const [yearMonths, setYearMonths] = useState([])
  const [years, setYears] = useState([])
  const [months, setMonths] = useState([]);
  const [calculationFiles, setCalculationFiles] = useState({
    AONLedger : null,
    RBSDetails : null,
    SiriusPoints : null,
    MOPMapping : null,
  })
  const [aonLedgerFiles, setAonLedgerFiles] = useState([]);
  const [siriusPointFiles, setSiriusPointFiles] = useState([]);
  const [rbsDetailsFiles, setRbsDetailsFiles] = useState([]);
  const [mopMappingFiles, setMopMappingFiles] = useState([]);
  const [openFileSelect, setOpenFileSelect] = useState(false)
  const [fileToRun, setFileToRun] = useState(null);
  const [isRunDisable, setIsRunDisable] = useState(false)
  const navigate = useNavigate();

  const handleCancelButton = () => {
    setMonth(null)
    setYear(null)
    loadData(true)
  }

  const resetFiles = () => {
    setCalculationFiles({
      AONLedger : null,
      RBSDetails : null,
      SiriusPoints : null,
      MOPMapping : null,
    })
  }

  const handleCancelRunButton = () => {
    setOpenFileSelect(false)
    setFileToRun(null)
    resetFiles()
  }

  const classes = useStyles();
  const handleVarianceResponse = (response) => {
    if (response.status === 200) {
      setPageState((old) => ({
        ...old,
        total: response?.data?.count,
        data: response.data.data || [],
      }));
    } else {
      setPageState((old) => ({
        ...old,
        total: response?.data?.count,
        data: [],
      }));
      setPaginationCount(0);
    }
  };

  useEffect(() => {
    loadYears();
    loadData();
    loadFiles();
  }, []);

  useEffect(() => {
    setPaginationCount(
      getPaginationCount(pageState?.total, pageState?.pageSize),
    );
  }, [pageState?.pageSize, pageState?.total]);

  useEffect(() => {
    loadData();
  }, [pageState?.pageSize, pageState?.page]);

  // Update months based on selected year
  useEffect(() => {
    if (year) {
      const uniqueMonths = yearMonths.find(data => data.year == year).months;
      
      const filteredMonthNames = uniqueMonths.map(monthValue => {
        return Months.find(m => m.value === monthValue);
      });

      setMonths(filteredMonthNames);
    } else {
      setMonths([]);
    }
  }, [year]);

  const [isReRun, setIsReRun] = useState(false)

  const handleRunButton = async (params, isReRunValue = false) => {
    setIsReRun(isReRunValue)
    setOpenFileSelect(true)
    if(params.row.calculation_files)
      setCalculationFiles({
        AONLedger: params.row.calculation_files.AONLedger,
        RBSDetails: params.row.calculation_files.RBSDetails,
        SiriusPoints: params.row.calculation_files.SiriusPoints,
        MOPMapping: params.row.calculation_files.MOPMapping
      })
    setFileToRun(params)
  }

  const runScript = async (params) => {
    setIsReportGenerating(true)
    setProcessingData([
      ...processingData,
      params.row.id,
    ])
    const response = await instance.post(`${RunAgeDeptReport}`, {
      file_name: params.row.file_name,
      file_month: params.row.month,
      file_year: params.row.year,
      id: params.row.id,
      is_rerun: isReRun,
      ...calculationFiles
    }).then((response) => {
      let index = pageState.data.findIndex(record => record.id == params.row.id);
      pageState.data[index].status = 'Processing';
      setPageState((old) => ({
        ...old,
        data: [
          ...pageState?.data
        ],
      }));
      setProcessingData(processingData.filter(data => data !== params.row.id));
      setIsReportGenerating(false)
      setOpenFileSelect(false)
      resetFiles();
      toast.success("Agedept report generating in background!", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
    }).catch((err) => {
      setOpenFileSelect(false)
      setProcessingData(processingData.filter(data => data !== params.row.id));
      setIsReportGenerating(false);
      toast.error("Something went wrong! : " + err.data.message, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
    });
  }

  const handleRefreshButton = async (params) => {
    
    setIsReportGenerating(true)
    setProcessingData([
      ...processingData,
      params.row.id,
    ])
    
    await instance.get(`${AgedReports}?id=${params.row.id}`).then((response) => {  
      let index = pageState.data.findIndex(record => record.id == params.row.id);
      const data = [...pageState.data]
      data[index] = response.data.data[0];

      setPageState((old) => ({
        ...old,
        data: [
          ...data
        ],
      }));

      setProcessingData(processingData.filter(data => data !== params.row.id));
      setIsReportGenerating(false)
    }).catch((err) => {
      setProcessingData(processingData.filter(data => data !== params.row.id));
      setIsReportGenerating(false);
    });
  }

  const handleDownloadButton = async  (params) => {
    const file_name = params.row.file_name
    setIsDownloadInProgress(true);
    setDownloadingData([
      ...downloadingData,
      params.row.id,
    ])
    try {
      instance
        .get(`${DownloadPolicyData}?file_name=${file_name}`, { responseType: "blob" })
        .then((response) => {
          const url = URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = url;
          const monthName = Months.find(m => m.value == params.row.month).name;
          const fileName = `Aged_Dept_Report_${monthName}_${params.row.year}_${format(new Date(), "dd-MM-yyyy")}`
          link.setAttribute("download", `${fileName}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          setIsDownloadInProgress(false);
          setDownloadingData(downloadingData.filter(data => data!==params.row.id))
          toast.success("File has been downloaded successfully", {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 1000,
          });
        }).catch((err) => {
          setIsDownloadInProgress(false);
          setDownloadingData(downloadingData.filter(data => data!==params.row.id))
          toast.error("File could not be downloaded", {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 1000,
          });
        });
    } catch (error) {
      setIsDownloadInProgress(false);
      toast.error("File could not be downloaded", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
    }
  };

  const handleFileClick = (fileName) => {
    localStorage.setItem('selectedFileName', fileName);    
    navigate(ROUTES.BTARD);
  }

  const handleFileSelect = (name, value) => {
    setCalculationFiles({
      ...calculationFiles,
      [name] : value
    })
  }

  useEffect(() => {
    console.log("Calculation files", calculationFiles);
    setIsRunDisable(calculationFiles.AONLedger === null || calculationFiles.MOPMapping === null || calculationFiles.RBSDetails === null || calculationFiles.SiriusPoints === null)
  }, [calculationFiles])
  

  const columns = [
    {
      field: "file_name",
      headerName: "File Name",
      headerAlign: "center",
      width: 300,
      cellClassName: "file-name-cell",
      renderCell: (params) => (
        <Tooltip
          title={params.row.file_name}
          componentsProps={{
            tooltip: {
              sx: tooltipStyle,
            },
          }}
          placement="bottom-end"
          PopperProps={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -10],
                },
              },
            ],
          }}
        >
        <span
          style={{ cursor: 'pointer', color: 'blue', textDecoration: 'none' }}
          onClick={() => handleFileClick(params.row.file_name)}
        >
          {params.row.file_name}
        </span>
        </Tooltip>
      )
    },
    {
      field: "month",
      headerName: "Month",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => Months.find(month => month.value === params.row.month).name        
    },
    {
      field: "year",
      headerName: "Year",
      headerAlign: "center",
      width: 80,
    },
    {
      field: "no_of_records",
      headerName: "#Records",
      headerAlign: "center",
      width: 80,
    },
    {
      field: "uploaded_date_time",
      headerName: "Uploaded Date",
      headerAlign: "center",
      width: 120,
      renderCell: (params) => <span>{customDateFormat(params.row.uploaded_date_time)}</span>
    },
    {
      field: "uploaded_by",
      headerName: "Uploaded By",
      headerAlign: "center",
      width: 120,
    },
    {
      field: "status",
      headerName: "Fetch Data",
      headerAlign: "center",
      width: 120,
      renderCell: (params) => {
        return (
          <>
            {
              params?.row?.status?.toLowerCase() === "processing" ? (
                isReportGenerating && processingData.includes(params.row.id) ? 
                    <CircularProgress size={35} style={{ color: "rgb(255, 90, 1)" }} />
                  :
                <>
                <PendingIcon
                  style={{ marginRight: 8 }}
                  titleAccess="Processing"
                />
                <ReplayIcon 
                  style={{ marginRight: 8, cursor: "pointer" }}
                  titleAccess="Refresh"
                  onClick={() => handleRefreshButton(params)}
                />
                </>
              ) : params?.row?.status?.toLowerCase() === "success" ? (
                <>
                  {(isDownloadInProgress && downloadingData.includes(params.row.id)) || 
                    (isReportGenerating && processingData.includes(params.row.id)) ? 
                    <CircularProgress size={35} style={{ color: "rgb(255, 90, 1)" }} />
                  :
                    <>
                      <CheckCircleIcon
                        style={{ marginRight: 8, cursor: "pointer", color: "green" }}
                        titleAccess="Success"
                      />
                      <DownloadIcon
                        style={{ marginRight: 8, cursor: "pointer" }}
                        onClick={() => handleDownloadButton(params)}
                        titleAccess="Download Age Dept Report"
                      />
                      <SettingsBackupRestoreIcon
                        style={{ marginRight: 8, cursor: "pointer" }}
                        onClick={() => handleRunButton(params, true)}
                        titleAccess="Re-run Age Dept Report"
                      />
                    </>
                  }
                </>
              )  : params?.row?.status?.toLowerCase() === "failed" ? (
                <>
                  {isReportGenerating && processingData.includes(params.row.id) ? 
                    <CircularProgress size={35} style={{ color: "rgb(255, 90, 1)" }} /> :
                    <>
                      <CancelIcon
                        style={{ marginRight: 8, cursor: "pointer", color:"red" }}
                        titleAccess="Failed"
                      />
                      <SettingsBackupRestoreIcon
                        style={{ marginRight: 8, cursor: "pointer" }}
                        onClick={() => handleRunButton(params, true)}
                        titleAccess="Re-run Age Dept Report"
                      />
                    </>
                  }
                </>
              )  : params?.row?.status?.toLowerCase() === "pending" ? (
                <>
                  {isReportGenerating && processingData.includes(params.row.id) ? 
                    <CircularProgress size={35} style={{ color: "rgb(255, 90, 1)" }} /> :
                    <PlayArrowIcon
                      style={{ marginRight: 8, cursor: "pointer" }}
                      onClick={() => handleRunButton(params, false)}
                      titleAccess="Run Age Dept Report"
                    />
                  }
                </>
              ) : ""
            }
          </>
        );
      },
    },
    {
      field: "progress",
      headerName: "Progress",
      headerAlign: "center",
      width: 120,
      renderCell: (params) => (
        <span>{ params.row.progress } %</span>
      )
    },
    {
      field: "last_run_by",
      headerName: "Last Run By",
      headerAlign: "center",
      width: 120,
    },
    {
      field: "last_run_time",
      headerName: "Last Run Time",
      headerAlign: "center",
      width: 120,
      renderCell: (params) => (
        <span>{ params.row.last_run_time ? format(new Date(params.row.last_run_time), "dd-MM-yyyy HH:m") : "-" }</span>
      )
    },
    {
      field: "error_message",
      headerName: "Error Message (If Any)",
      headerAlign: "center",
      width: 250,
      renderCell: (params) => (
        <Tooltip
          title={params?.row?.status?.toLowerCase() === "failed" && params.row.error_message ? params.row.error_message : "N/A"}
          componentsProps={{
            tooltip: {
              sx: tooltipStyle,
            },
          }}
          placement="bottom-end"
          PopperProps={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -60],
                },
              },
            ],
          }}
        >
        <span style={{textWrap: 'auto'}}>{ params?.row?.status?.toLowerCase() === "failed" && params.row.error_message ? params.row.error_message : "N/A"}</span>
        </Tooltip>
      )
    },
  ];



  // skip 0 means page 1
  const currentSkipNumber = () => {
    return pageState?.page === 1
      ? 0
      : pageState?.page === 0
      ? pageState?.page
      : pageState?.page - 1;
  };

  useEffect(() => {
    setPaginationCount(getPaginationCount(pageState?.total, pageState?.pageSize));
  }, [pageState?.total]);

  const loadFiles = async () => {
    try {
      const response = await instance.get(`${GetCalculationFiles}`);
      const files = response.data;
      setAonLedgerFiles(files.AON_Ledger_files)
      setSiriusPointFiles(files.Sirius_Data_files)
      setRbsDetailsFiles(files.RBS_Details_files)
      setMopMappingFiles(files.MOP_mapping_files)
    } catch (error) {
      console.error("Error getting Files : ", error)
    }
  }

  const loadYears = async () => {
    try {
      
      const response = await instance.get(`${GetAgedDeptYears}`);
      const data = response.data;
      setYearMonths(data)
      const yearsList = data.map(item => item.year);
      setYears(yearsList);
    } catch (error) {
      console.error("Error getting Year : ", error)
    }
  }


  const loadData = async (isClear = false) => {
    try {
      setPageState((old) => ({
        ...old,
        isLoading: true,
      }));

      let queryParams = `skip=${currentSkipNumber()}&pageSize=${
        pageState?.pageSize
      }`;
      
      if(!isClear && month){
        queryParams = `${queryParams}&month=${month}`
      }
      
      if(!isClear && year){
        queryParams = `${queryParams}&year=${year}`
      }

      const response = await instance.get(`${AgedReports}?${queryParams}`);

      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));

      handleVarianceResponse(response);
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("err", err);
    }
  };

  const handleEnter = (e) => {
    if (e.code === "Enter") {
      loadData();
    }
  };
  
  return (
    <Box style={agedReportContainer} >
      <div style={agedReportText}>
        <Typography
          variant="h3"
          style={{
            color: "#FF5A01",
            fontSize: "24px",
            margin: "10px 0px 15px 0px",
          }}
        >
          Agedebt Calculation
        </Typography>
      </div>

      <Grid
        display={"flex"}
        flexDirection={"row"}
        gap={"10px"}
        container
      >
        <Grid item display={"flex"} flexDirection={"column"} gap={"8px"}>
          <FormLabel>Year</FormLabel>
          <div onKeyUp={(e) => handleEnter(e)}>
          <CommonSelect
            placeholder="Select Year"
            value={year}
            handleChange={(event, value, reason) => setYear(value) }
            options={years || []}
            customStyles={{ width: "230px" }}
          />
          </div>
        </Grid>

        <Grid item display={"flex"} flexDirection={"column"} gap={"8px"}>
          <FormLabel>Month</FormLabel>
          <div onKeyUp={(e) => handleEnter(e)}>
          <CommonSelect
            placeholder="Select Month"
            value={months.find(option => option.value === month) || null}
            handleChange={(event, value, reason) => setMonth(value ? value.value : null) }
            options={months}
            getOptionLabel={(option) => option.name ?? option} // Use 'name' as the label for each option
            getOptionSelected={(option, value) => option.value === value} // Compare 'value' of option and selected value
            isOptionEqualToValue={(option, value) => option.value === value} // Ensure matching 'value'
            customStyles={{ width: "230px" }}
          />
          </div>
        </Grid>
        
        <Grid
          item
          alignSelf={"space-between"}
          justifyContent={"space-between"}
          display={"flex"}
          flexDirection={"row"}
          gap={"10px"}
          paddingTop={"28px"}
        >
          <CommonButton
            text="Search"
            handleClick={() => loadData()}
            hoverColor="#EE3F00"
            disabled={
              month === null || year === null
            }
          />
          <CommonButton
            text="Clear"
            handleClick={handleCancelButton}
            hoverColor="#EE3F00"
          />
        </Grid>
      </Grid>

      <Grid item>
              <div
                style={{
                  height: "calc(100vh - 313px)",
                  position: "relative",
                  display: "flex",
                  flexDirection: "row-reverse",
                  marginTop:"8px"
                }}
              >
                <DataGrid
                  className={classes.pagination}
                  loading={pageState?.isLoading}
                  rows={pageState?.data ?? []}
                  columns={columns}
                  rowCount={pageState?.total}
                  keepNonExistentRowsSelected
                  disableSelectionOnClick
                  pagination
                  paginationMode="server"
                  getRowId={(row) => pageState?.data.indexOf(row)}
                  page={pageState.page - 1}
                  pageSize={pageState?.pageSize ?? 25}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  onPageSizeChange={(newPageSize) => {
                    return setPageState((old) => {
                      setPaginationCount(
                        getPaginationCount(pageState?.total, newPageSize),
                      );
                      return { ...old, pageSize: newPageSize, page: 1 };
                    });
                  }}
                  sx={{
                    "& .MuiDataGrid-cellContent": {
                      textAlign: "start",
                    },
                    "& .MuiDataGrid-columnHeader[data-field='Comments']": {
                      minWidth: toggle?.isOpen
                        ? "209.137px !important"
                        : "209.137px !important",
                    },
                  }}
                />
                <Stack
                  style={{
                    position: "absolute",
                    bottom: "8px",
                  }}
                >
                  <CommonPaginationContainer
                    count={paginationCount + 1}
                    pageState={pageState}
                    setPageState={setPageState}
                  />
                </Stack>
              </div>
      </Grid>

      <Dialog open={openFileSelect} onClose={handleCancelRunButton} fullWidth>
        {isReportGenerating &&
          <Box sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgb(255 255 255 / 62%)',
            zIndex: 9,
          }}>
            <CircularProgress size={35} style={{ color: "rgb(255, 90, 1)" }} /> 
          </Box>
        }
        
          <DialogTitle style={{ background: "#FCFCFC" }}>
            Select Files
            <IconButton
              aria-label="close"
              onClick={handleCancelRunButton}
              style={{ position: "absolute", right: 12, top: 12 }}
              disabled={isReportGenerating}
            >
              <CloseIcon className="CloseIconStyle" />
            </IconButton>
          </DialogTitle>
          <DialogContent>
          <Grid container>
            <Grid item xs={12} md={12}>
              <p style={{ fontSize: 13, color: "#17191B" }}>
                Select AON Ledger File
              </p>
              <CommonSelect
                handleChange={(event, value, reason) =>
                  handleFileSelect("AONLedger", value)
                }
                name="AON Ledger"
                options={aonLedgerFiles?.map((option) => option) || []}
                placeholder="Select AON Ledger"
                value={calculationFiles.AONLedger}
                customStyles={{ width: "395px" }}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <p style={{ fontSize: 13, color: "#17191B" }}>
                Select RBS Details File
              </p>
              <CommonSelect
                handleChange={(event, value, reason) =>
                  handleFileSelect("RBSDetails", value)
                }
                name="RBS Details"
                options={rbsDetailsFiles?.map((option) => option) || []}
                placeholder="Select RBS Details"
                value={calculationFiles.RBSDetails}
                customStyles={{ width: "395px" }}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <p style={{ fontSize: 13, color: "#17191B" }}>
                Select Sirius Point File
              </p>
              <CommonSelect
                handleChange={(event, value, reason) =>
                  handleFileSelect("SiriusPoints", value)
                }
                name="Sirius Points"
                options={siriusPointFiles?.map((option) => option) || []}
                placeholder="Select Sirius Points"
                value={calculationFiles.SiriusPoints}
                customStyles={{ width: "395px" }}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <p style={{ fontSize: 13, color: "#17191B" }}>
                Select MOP Mapping File
              </p>
              <CommonSelect
                handleChange={(event, value, reason) =>
                  handleFileSelect("MOPMapping", value)
                }
                name="MOP Mapping"
                options={mopMappingFiles?.map((option) => option) || []}
                placeholder="Select MOP Mapping"
                value={calculationFiles.MOPMapping}
                customStyles={{ width: "395px" }}
              />
            </Grid> 
          </Grid>
            
          </DialogContent>
          <DialogActions style={{ justifyContent: "flex-end" }}>
            <CommonButton
              text="Run"
              handleClick={() => runScript(fileToRun)}
              disabled={isReportGenerating || isRunDisable}
              hoverColor="#FF5A01"
              variant="outlined"
            />
            <CommonButton
              text="Cancel"
              handleClick={handleCancelRunButton}
              disabled={isReportGenerating}
              sx={{
                borderRadius: 100,
                backgroundColor: "#ffff",
                color: "#000000",
                border: "1px solid #BCBDBF",
              }}
              variant="outlined"
            />
          </DialogActions>
        
        
      </Dialog>      
    </Box>
  );
};
